import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

// Components
import { Title, Alert } from 'wg-fe-ui';
import { Box } from '@wegroup/design-system';

// Toolkit components
import VinLookup from '../../../../../../common/src/components/VinLookup';

// Types
import { Car } from '../../../../../../../types/SMT';
import {
  ReportData,
  VehicleResponseProps,
} from '../../../../../../../types/ToolkitDashboard';
import ToolkitVinReportModalContainer from '../../../../../../toolkit/src/components/ToolkitVinReportModalContainer';
import { request } from '../../../../../../common/src/legacy/services/httpSessionService';
import cogoToast from 'cogo-toast';
import QuestionGroupActions from '../../../ui/advisory-flow/components/question-group-components/QuestionGroupActions';
import AdvisoryFlowMainContainer from '../../../ui/advisory-flow/components/app-shell/AdvisoryFlowMainContainer';
import { getBrandNameFilter } from '../../../../utils/carUtils';
import QuestionGroupAssistantQuestion from '../../../ui/advisory-flow/components/question-group-components/QuestionGroupAssistantQuestion';
import QuestionGroupBody from '../../../ui/advisory-flow/components/question-group-components/QuestionGroupBody';
import useTracking from '../../../../../../common/src/hooks/tracking/useTracking';
import useAuth from '../../../../../../app/src/features/authentication/hooks/useAuth';

interface Props {
  nextSubView: (arg0: string) => void;
  handleChange: (arg0: Car) => void;
  previousSubView: () => void;
  initialVin?: string;
}

const VinSearch: React.FC<React.PropsWithChildren<Props>> = ({
  handleChange,
  nextSubView,
  previousSubView,
  initialVin,
}) => {
  const { t } = useTranslation();
  const [reportData, setReportData] = useState<ReportData>();
  const [showReportModal, setShowReportModal] = useState<boolean>(false);
  const [isVinSupported, setIsVinSupported] = useState<boolean>(true);
  const [brandNameFilter, setBrandNameFilter] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { campaign } = useAuth();
  const { trackEvent } = useTracking();

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      let filter: string[] = [];
      if (campaign) filter = (await getBrandNameFilter(campaign)) || [];
      setBrandNameFilter(filter);
      setIsLoading(false);
    })();
  }, []);

  const mapVinSearchResponse = (car: Car, images: string[]) => {
    return { ...car, images: images };
  };

  const handleOnChange = async (vehicle: VehicleResponseProps[]) => {
    if (
      vehicle[0].car.brand &&
      brandNameFilter.length > 0 &&
      !brandNameFilter
        .map((name) => name.toLowerCase())
        .includes(vehicle[0].car.brand.toLowerCase())
    ) {
      if (!brandNameFilter.includes(vehicle[0].car.brand.toLowerCase()))
        cogoToast.error(t('The car brand of the given VIN is not permitted'));
      setIsVinSupported(false);
    } else if (vehicle[0].car) {
      const [resp, status] = await request({ path: vehicle[0].self });
      if (status === 200) {
        const { car } = resp?.domain_car;
        handleChange(mapVinSearchResponse(car, resp?.data?.images));
        nextSubView('vehicleData');
        setIsVinSupported(true);
      } else cogoToast.error(t('Could not retrieve vehicle info'));
    }
    setIsLoading(false);
  };

  const Question = () => (
    <QuestionGroupAssistantQuestion
      question={t('Please provide us with the VIN-number of your vehicle')}
      description={t(
        'By entering your VIN-number, we wil be able to find all the information about the vehicle',
      )}
    />
  );

  return (
    <AdvisoryFlowMainContainer data-test-id="ProdFac_VinSearch">
      {showReportModal && (
        <ToolkitVinReportModalContainer
          vin={reportData?.vin}
          rawResponse={reportData?.resp}
          setShowModal={setShowReportModal}
        />
      )}
      <Question />
      <QuestionGroupBody>
        <Box maxW="1024px" mx="auto">
          <VinContainer data-test-id="ProdFac_VinSearch_Container">
            <Title>{t('Whats your VIN-number?')}</Title>
            <VinLookup
              isLoading={isLoading}
              setIsLoading={(loading) => loading && setIsLoading(loading)}
              onChange={handleOnChange}
              onError={() => setIsLoading((prev) => prev && false)}
              initialValue={initialVin}
              onNotFoundClick={(vin, resp) => {
                trackEvent('Report VIN Problem');
                setReportData({ vin, resp });
                setShowReportModal(true);
                setIsLoading(false);
              }}
            />
          </VinContainer>
          {brandNameFilter.length > 0 && (
            <StyledAlert type={!isVinSupported ? 'error' : 'warning'}>
              {t('Only the following brands are permitted')}:
              <BrandList>
                {brandNameFilter.map((brand: string) => (
                  <li key={`list_item_${brand}`}>{brand}</li>
                ))}
              </BrandList>
            </StyledAlert>
          )}
        </Box>
      </QuestionGroupBody>

      <QuestionGroupActions onBack={previousSubView} hideContinueButton />
    </AdvisoryFlowMainContainer>
  );
};

const StyledAlert = styled(Alert)`
  margin-top: 3.2rem;
`;

const BrandList = styled.ul`
  margin-top: 0.8rem;
  > li {
    list-style: disc;
    margin-left: 1.6rem;
  }
`;

const VinContainer = styled.div`
  width: 100%;
  margin: 2.4rem auto;
  > h1 {
    margin-bottom: 1rem;
  }
`;

export default VinSearch;
